import React, { useState } from 'react';
import { HelpCircle, X } from 'lucide-react';

/**
 * Mar 4, 2026 (v6.9.7) — Visual Signal Legend
 *
 * Small "?" icon that opens a popover listing every colored signal
 * WriteRight PCM+ uses on the provider view. Doubles as onboarding
 * for new users and as an in-app patent artifact (each row cites the
 * BAA / Privacy / Compliance clause the signal implements).
 *
 * Kept self-contained so it can be slotted into any header row without
 * pulling in Radix / Shadcn primitives; also renders fast (no portal,
 * no focus-trap machinery, just a controlled floating panel).
 */
const LegendPopover = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center justify-center w-7 h-7 rounded-full text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        title="What do the colors mean?"
        data-testid="signal-legend-btn"
      >
        <HelpCircle className="w-4 h-4" />
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            data-testid="signal-legend-backdrop"
          />
          {/* Panel */}
          <div
            className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-lg max-h-[85vh] overflow-y-auto rounded-xl border border-border bg-card shadow-2xl"
            data-testid="signal-legend-panel"
          >
            <div className="sticky top-0 bg-card border-b border-border px-4 py-3 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-bold">Visual Signal Legend</h2>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  What every color and pill on this page means
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1 rounded hover:bg-accent"
                data-testid="signal-legend-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 space-y-3 text-xs">
              {[
                {
                  swatch: <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" />,
                  label: 'Emerald dot',
                  desc: 'Field auto-detected from your dictation (voice-first workflow). Edit the field to override — the dot disappears.',
                  where: 'Age · Sex · Chief Complaint · Status',
                },
                {
                  swatch: <span className="inline-block w-3 h-3 rounded-sm border border-emerald-500/60 bg-emerald-500/10" />,
                  label: 'Emerald input border',
                  desc: 'Same meaning as the dot — this input was populated automatically. Tint clears the moment you type.',
                  where: 'Any auto-fillable input',
                },
                {
                  swatch: <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium border border-amber-500/40 bg-amber-500/10 text-amber-300">HIPAA</span>,
                  label: 'Amber HIPAA chip',
                  desc: 'The demographic scrubber removed a name / SSN / MRN / DOB from your dictation before it was sent to the AI. Clinical facts kept, identifiers stripped.',
                  where: 'Above the notes textarea when a scrub fires',
                },
                {
                  swatch: <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium border border-cyan-500/40 bg-cyan-500/10 text-cyan-300">68 y/o M · ADSM</span>,
                  label: 'Cyan Verified Demographics pill',
                  desc: 'The demographic values embedded in the generated note. Compare this pill to your intake bar — if they match, the note is safe to copy to Genesis.',
                  where: 'Adjacent to the clinical note title after generation',
                },
                {
                  swatch: <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium border border-amber-500/40 bg-amber-500/10 text-amber-300">Offline</span>,
                  label: 'Amber Offline queue badge',
                  desc: 'You lost connectivity mid-encounter. Drafts are being queued to your local IndexedDB and will auto-sync when the network returns. No PHI leaves the device while offline.',
                  where: 'Top of Speed Chart panel · Global banner',
                },
                {
                  swatch: <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium border border-purple-500/40 bg-purple-500/10 text-purple-300">M17.9</span>,
                  label: 'Purple ICD-10 suggestion pill',
                  desc: 'Live diagnostic code suggested by the AI as you type. Click to add to your code stack; the E/M level is reasoned from the same input.',
                  where: 'Live ICD-10 dropdown below the notes textarea',
                },
                {
                  swatch: <span className="inline-block w-2 h-2 rounded-full bg-red-500" />,
                  label: 'Red dot',
                  desc: 'A demographic mismatch was detected between your intake bar and the generated note, or a required field is missing. Fix before copying to Genesis.',
                  where: 'Any input · Verified Demographics pill',
                },
                {
                  swatch: <span className="inline-block w-2 h-2 rounded-full bg-yellow-400" />,
                  label: 'Yellow dot',
                  desc: 'Non-blocking advisory — a soft validation warning that does NOT stop generation but is worth reviewing.',
                  where: 'Various validation surfaces',
                },
                {
                  swatch: <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium border border-emerald-500/40 bg-emerald-500/10 text-emerald-300">Auto</span>,
                  label: 'Emerald Auto tag',
                  desc: 'A field / preference the system inferred and applied without asking. Always overridable.',
                  where: 'Anywhere the platform makes a decision for you',
                },
                {
                  swatch: <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium border border-cyan-500/40 bg-cyan-500/10 text-cyan-300">Transient</span>,
                  label: 'Trust-badge pills (landing page)',
                  desc: 'Compliance posture summary — each pill cites a specific clause in the BAA / Privacy Policy (see Compliance One-Pager and Security FAQ).',
                  where: 'SiteGate footer',
                },
              ].map((row, i) => (
                <div key={i} className="flex items-start gap-3 pb-3 border-b border-border/50 last:border-b-0" data-testid={`legend-row-${i}`}>
                  <div className="w-16 shrink-0 flex justify-center pt-1">{row.swatch}</div>
                  <div className="flex-1">
                    <div className="font-semibold text-foreground">{row.label}</div>
                    <p className="text-muted-foreground mt-0.5 leading-snug">{row.desc}</p>
                    <p className="text-[10px] text-muted-foreground/70 mt-1 italic">Seen: {row.where}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="sticky bottom-0 bg-card border-t border-border px-4 py-2 text-[10px] text-muted-foreground text-center">
              Cross-references: <span className="font-medium">BAA §2.5, §3.2(d), §5.2</span> · Privacy Policy §3 · Compliance One-Pager
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default LegendPopover;
